import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, X, ShieldCheck, BadgeCheck, Lock, Crown } from "lucide-react";
import Navbar from "../components/Navbar";
import FilmCard from "../components/FilmCard";
import api, { formatApiErrorDetail } from "../api/client";
import { useAuth } from "../context/AuthContext";
import {
    CONTENT_TYPES,
    RIGHTS_TYPES,
    REGIONS,
    LANGUAGES,
    BUDGET_RANGES,
    STAGES,
    GENRES,
    SUB_GENRES,
    COPYRIGHT_STATUSES,
} from "../lib/filters";

const ANY = "Any";

const STATUSES = [
    { k: "all", label: "All" },
    { k: "available", label: "Available" },
    { k: "under_offer", label: "Under Offer" },
    { k: "sold", label: "Sold" },
];

const defaultFilters = () => ({
    content_type: ANY,
    rights_type: ANY,
    region: ANY,
    language: ANY,
    stage: ANY,
    genre: ANY,
    sub_genre: ANY,
    budget: ANY,
    copyright_status: ANY,
    chain_of_title_verified: false,
    exclusivity: ANY,
    status: "all",
});

export default function Catalog() {
    const [films, setFilms] = useState([]);
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState(defaultFilters());
    const [showPanel, setShowPanel] = useState(false);

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const params = {};
            if (search.trim()) params.search = search.trim();
            if (filters.content_type !== ANY) params.content_type = filters.content_type;
            if (filters.rights_type !== ANY) params.rights_type = filters.rights_type;
            if (filters.region !== ANY && filters.region !== "Global")
                params.region = filters.region;
            if (filters.language !== ANY) params.language = filters.language;
            if (filters.stage !== ANY) params.stage = filters.stage;
            if (filters.genre !== ANY) params.genre = filters.genre;
            if (filters.sub_genre !== ANY) params.sub_genre = filters.sub_genre;
            if (filters.copyright_status !== ANY)
                params.copyright_status = filters.copyright_status;
            if (filters.chain_of_title_verified) params.chain_of_title_verified = true;
            if (filters.exclusivity !== ANY) params.exclusivity = filters.exclusivity;
            if (filters.budget !== ANY) {
                const b = BUDGET_RANGES.find((x) => x.key === filters.budget);
                if (b) {
                    params.price_min = b.min;
                    if (b.max != null) params.price_max = b.max;
                }
            }
            if (filters.status !== "all") params.status_filter = filters.status;
            const { data } = await api.get("/films", { params });
            setFilms(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

    const availableSubGenres = useMemo(() => {
        if (filters.genre === ANY) return [];
        return SUB_GENRES[filters.genre] || [];
    }, [filters.genre]);

    const activeChips = useMemo(() => {
        const chips = [];
        const push = (key, label, reset) => chips.push({ key, label, reset });
        if (filters.content_type !== ANY) push("content_type", filters.content_type, () => set("content_type")(ANY));
        if (filters.rights_type !== ANY) push("rights_type", filters.rights_type, () => set("rights_type")(ANY));
        if (filters.region !== ANY) push("region", filters.region, () => set("region")(ANY));
        if (filters.language !== ANY) push("language", filters.language, () => set("language")(ANY));
        if (filters.stage !== ANY) push("stage", filters.stage, () => set("stage")(ANY));
        if (filters.genre !== ANY) push("genre", filters.genre, () => set("genre")(ANY));
        if (filters.sub_genre !== ANY) push("sub_genre", filters.sub_genre, () => set("sub_genre")(ANY));
        if (filters.budget !== ANY) {
            const b = BUDGET_RANGES.find((x) => x.key === filters.budget);
            push("budget", b?.label || filters.budget, () => set("budget")(ANY));
        }
        if (filters.copyright_status !== ANY) push("copyright_status", `© ${filters.copyright_status}`, () => set("copyright_status")(ANY));
        if (filters.chain_of_title_verified) push("cot", "Chain-of-title verified", () => set("chain_of_title_verified")(false));
        if (filters.exclusivity !== ANY) push("exclusivity", filters.exclusivity, () => set("exclusivity")(ANY));
        if (filters.status !== "all") push("status", STATUSES.find((s) => s.k === filters.status)?.label, () => set("status")("all"));
        return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    return (
        <div className="min-h-screen bg-ink-300">
            <Navbar />
            <div className="max-w-[1500px] mx-auto px-4 md:px-10 pt-10 md:pt-16 pb-16 md:pb-24">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8"
                >
                    <div>
                        <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-platinum mb-2 md:mb-3">
                            Marketplace
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
                            Rights <span className="silver-text italic">available for transfer</span>
                        </h1>
                        <p className="mt-3 md:mt-4 text-silver-400 max-w-xl text-sm md:text-base">
                            {films.length} {films.length === 1 ? "title" : "titles"} • vetted chain-of-title • exclusive & non-exclusive rights bundles.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                load();
                            }}
                            className="glass flex items-center gap-2 px-4 py-2 rounded-sm w-full sm:min-w-[320px] sm:w-auto"
                        >
                            <Search className="w-4 h-4 text-platinum" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search title, cast, keyword…"
                                className="bg-transparent flex-1 text-sm text-white placeholder:text-silver-500 focus:outline-none py-1"
                                data-testid="catalog-search"
                            />
                            <button
                                type="submit"
                                className="text-xs uppercase tracking-widest text-silver-300 hover:text-platinum"
                                data-testid="catalog-search-btn"
                            >
                                Search
                            </button>
                        </form>
                        <button
                            onClick={() => setShowPanel((v) => !v)}
                            className="glass flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs uppercase tracking-widest text-silver-200 hover:text-white"
                            data-testid="catalog-filters-toggle"
                        >
                            <SlidersHorizontal className="w-4 h-4 text-platinum" />
                            Filters
                            {activeChips.length > 0 && (
                                <span className="bg-platinum text-ink-300 rounded-full px-2 text-[10px] ml-1">
                                    {activeChips.length}
                                </span>
                            )}
                        </button>
                    </div>
                </motion.div>

                {/* Role-aware banner */}
                {!user && (
                    <div className="mb-8 border border-platinum/30 bg-platinum/5 rounded-sm p-4 md:p-5 flex flex-wrap items-center gap-3 md:gap-5" data-testid="catalog-guest-banner">
                        <Lock className="w-5 h-5 text-platinum shrink-0" />
                        <div className="flex-1 min-w-[240px]">
                            <div className="text-silver-200 text-sm">You're browsing as a guest. Asking prices and deal rooms are hidden until you sign in.</div>
                            <div className="text-[11px] text-silver-500 tracking-widest uppercase mt-1">Browse freely · price on request after verification</div>
                        </div>
                        <Link to="/register?role=buyer" className="bg-platinum hover:bg-white text-ink-400 px-4 py-2 rounded-sm text-xs tracking-widest uppercase" data-testid="guest-cta-buyer">Join as Buyer</Link>
                        <Link to="/login" className="border border-white/20 hover:border-platinum text-silver-200 px-4 py-2 rounded-sm text-xs tracking-widest uppercase">Sign in</Link>
                    </div>
                )}
                {user?.role === "creator" && (
                    <div className="mb-8 border border-white/10 bg-white/[0.02] rounded-sm p-4 md:p-5 flex flex-wrap items-center gap-3 md:gap-5" data-testid="catalog-creator-banner">
                        <Crown className="w-5 h-5 text-platinum shrink-0" />
                        <div className="flex-1 min-w-[240px]">
                            <div className="text-silver-200 text-sm">You're viewing the marketplace as a creator. Study comps and asking prices, then list your own film.</div>
                            <div className="text-[11px] text-silver-500 tracking-widest uppercase mt-1">Purchase flow is buyer-only</div>
                        </div>
                        <Link to="/creator" className="bg-platinum hover:bg-white text-ink-400 px-4 py-2 rounded-sm text-xs tracking-widest uppercase">Open Upload Studio</Link>
                    </div>
                )}

                {/* Active chips + status */}
                <div className="flex flex-wrap items-center gap-2 mb-5">
                    {STATUSES.map((s) => (
                        <button
                            key={s.k}
                            onClick={() => set("status")(s.k)}
                            className={`px-3 py-1.5 rounded-sm tracking-widest uppercase text-xs border transition-colors ${
                                filters.status === s.k
                                    ? "border-platinum text-platinum bg-platinum/10"
                                    : "border-white/10 text-silver-400 hover:text-white"
                            }`}
                            data-testid={`filter-status-${s.k}`}
                        >
                            {s.label}
                        </button>
                    ))}
                    <div className="w-px h-5 bg-white/10 mx-1" />
                    {activeChips.map((c) => (
                        <button
                            key={c.key}
                            onClick={c.reset}
                            className="inline-flex items-center gap-1.5 bg-platinum/15 border border-platinum/30 text-platinum rounded-sm px-3 py-1.5 text-xs"
                            data-testid={`chip-${c.key}`}
                        >
                            {c.label}
                            <X className="w-3 h-3" />
                        </button>
                    ))}
                    {activeChips.length > 0 && (
                        <button
                            onClick={() => setFilters(defaultFilters())}
                            className="text-xs text-silver-400 hover:text-white underline underline-offset-4 ml-1"
                            data-testid="chip-clear-all"
                        >
                            Clear all
                        </button>
                    )}
                </div>

                {/* FILTER PANEL */}
                <AnimatePresence>
                    {showPanel && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden mb-8"
                            data-testid="filter-panel"
                        >
                            <div className="glass rounded-sm p-5 md:p-7 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                                <Dropdown
                                    label="Content Type"
                                    value={filters.content_type}
                                    options={[ANY, ...CONTENT_TYPES]}
                                    onChange={set("content_type")}
                                    testid="filter-content-type"
                                />
                                <Dropdown
                                    label="Rights Type"
                                    value={filters.rights_type}
                                    options={[ANY, ...RIGHTS_TYPES]}
                                    onChange={set("rights_type")}
                                    testid="filter-rights-type"
                                />
                                <Dropdown
                                    label="Region"
                                    value={filters.region}
                                    options={[ANY, ...REGIONS]}
                                    onChange={set("region")}
                                    testid="filter-region"
                                />
                                <Dropdown
                                    label="Language"
                                    value={filters.language}
                                    options={[ANY, ...LANGUAGES]}
                                    onChange={set("language")}
                                    testid="filter-language"
                                />
                                <Dropdown
                                    label="Stage"
                                    value={filters.stage}
                                    options={[ANY, ...STAGES]}
                                    onChange={set("stage")}
                                    testid="filter-stage"
                                />
                                <Dropdown
                                    label="Genre"
                                    value={filters.genre}
                                    options={[ANY, ...GENRES]}
                                    onChange={(v) => {
                                        setFilters((f) => ({ ...f, genre: v, sub_genre: ANY }));
                                    }}
                                    testid="filter-genre"
                                />
                                <Dropdown
                                    label="Sub-genre"
                                    value={filters.sub_genre}
                                    disabled={availableSubGenres.length === 0}
                                    options={[ANY, ...availableSubGenres]}
                                    onChange={set("sub_genre")}
                                    testid="filter-sub-genre"
                                />
                                <Dropdown
                                    label="Budget range"
                                    value={filters.budget}
                                    options={[ANY, ...BUDGET_RANGES.map((b) => b.key)]}
                                    labelFor={(k) =>
                                        k === ANY ? ANY : BUDGET_RANGES.find((b) => b.key === k)?.label || k
                                    }
                                    onChange={set("budget")}
                                    testid="filter-budget"
                                />
                                <Dropdown
                                    label="Copyright status"
                                    value={filters.copyright_status}
                                    options={[ANY, ...COPYRIGHT_STATUSES]}
                                    onChange={set("copyright_status")}
                                    testid="filter-copyright-status"
                                />
                                <Dropdown
                                    label="Exclusivity"
                                    value={filters.exclusivity}
                                    options={[ANY, "Exclusive", "Non-exclusive"]}
                                    onChange={set("exclusivity")}
                                    testid="filter-exclusivity"
                                />
                                <label className="flex items-center gap-2 text-xs text-silver-300 mt-6">
                                    <input
                                        type="checkbox"
                                        checked={filters.chain_of_title_verified}
                                        onChange={(e) =>
                                            set("chain_of_title_verified")(e.target.checked)
                                        }
                                        className="accent-platinum"
                                        data-testid="filter-cot"
                                    />
                                    <ShieldCheck className="w-4 h-4 text-platinum" />
                                    Chain-of-title verified only
                                </label>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {err && (
                    <div className="text-red-400 text-sm mb-4" data-testid="catalog-error">
                        {err}
                    </div>
                )}

                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <div
                                key={i}
                                className="aspect-[2/3] bg-white/5 rounded-sm animate-pulse"
                            />
                        ))}
                    </div>
                ) : films.length === 0 ? (
                    <div className="text-center py-24 text-silver-400">
                        <BadgeCheck className="w-8 h-8 mx-auto mb-4 text-platinum/60" />
                        No titles match those filters.
                        <div className="mt-3">
                            <button
                                onClick={() => setFilters(defaultFilters())}
                                className="text-platinum underline underline-offset-4"
                            >
                                Clear filters
                            </button>
                        </div>
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6"
                        data-testid="catalog-grid"
                    >
                        {films.map((f, i) => (
                            <FilmCard key={f.id} film={f} index={i} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Dropdown({ label, value, options, onChange, testid, disabled, labelFor }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-widest text-silver-500 mb-1.5">
                {label}
            </label>
            <select
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-ink-200 border border-white/10 focus:border-platinum/60 focus:outline-none text-white text-sm rounded-sm px-3 py-2 disabled:opacity-40"
                data-testid={testid}
            >
                {options.map((o) => (
                    <option key={o} value={o} className="bg-ink-200 text-white">
                        {labelFor ? labelFor(o) : o}
                    </option>
                ))}
            </select>
        </div>
    );
}
